import { Component, Input } from '@angular/core';

export interface PipelineTrackerStage {
  key: string;
  label: string;
  color?: string | null;
  verificationRequired?: boolean;
  stepType?: string;
}

type PipelineTrackerInputStage =
  | string
  | PipelineTrackerStage
  | {
      status: string;
      label: string;
      color?: string | null;
      verificationRequired?: boolean;
  stepType?: string;
    };

@Component({
  selector: 'app-pipeline-tracker',
  standalone: false,
  templateUrl: './pipeline-tracker.html',
  styleUrl: './pipeline-tracker.scss',
})
export class PipelineTracker {
  @Input() stages: PipelineTrackerInputStage[] = [];
  @Input() current: string | null = null;
  @Input() cancelled = false;

  normalizedStages(): PipelineTrackerStage[] {
    return this.stages.map((stage) => {
      if (typeof stage === 'string') {
        return { key: stage, label: stage };
      }

      const key = 'key' in stage ? stage.key : stage.status;
      return {
        key,
        label: stage.label,
        color: stage.color,
        verificationRequired: stage.verificationRequired,
        stepType: stage.stepType,
      };
    });
  }

  isPast(stage: PipelineTrackerStage): boolean {
    const stages = this.normalizedStages();
    const currentIndex = stages.findIndex((item) => item.key === this.current || item.label === this.current);
    const stageIndex = stages.findIndex((item) => item.key === stage.key);
    return currentIndex >= 0 && stageIndex < currentIndex && !this.cancelled;
  }

  isCurrent(stage: PipelineTrackerStage): boolean {
    return (stage.key === this.current || stage.label === this.current) && !this.cancelled;
  }

  stageBackground(stage: PipelineTrackerStage): string | null {
    // Default state colors belong to the theme; only configured colors need an inline override.
    return this.isCurrent(stage) ? this.resolveColor(stage.color) : null;
  }

  stageForeground(stage: PipelineTrackerStage): string | null {
    const background = this.stageBackground(stage);
    if (!background) return null;
    return this.isLightColor(background) ? '#182433' : '#ffffff';
  }

  private resolveColor(value: string | null | undefined): string | null {
    const color = value?.trim().toLowerCase();
    if (!color) return null;
    const semanticColors: Record<string, string> = {
      primary: '#206bc4',
      secondary: '#667382',
      success: '#2fb344',
      warning: '#f59f00',
      danger: '#d63939',
      info: '#4299e1',
      azure: '#4299e1',
      blue: '#206bc4',
      green: '#2fb344',
      red: '#d63939',
      orange: '#f76707',
      yellow: '#f59f00',
      purple: '#ae3ec9',
    };
    if (semanticColors[color]) return semanticColors[color];
    return /^#[0-9a-f]{6}$/i.test(color) ? color : null;
  }

  private isLightColor(color: string): boolean {
    if (!/^#[0-9a-f]{6}$/i.test(color)) return false;
    const red = Number.parseInt(color.slice(1, 3), 16);
    const green = Number.parseInt(color.slice(3, 5), 16);
    const blue = Number.parseInt(color.slice(5, 7), 16);
    return (red * 299 + green * 587 + blue * 114) / 1000 > 170;
  }
}
